import type { FaqSection } from '~/graphql/generated/graphql';
import type { UseSectionFaqList } from '~/types/Faq';

import { fetchLearningFaqsBySection, learningFaqFragment } from '~/graphql/documents/campaigns';
import { useFragment } from '~/graphql/generated';
import { LearningFaqOrderByColumn, SortOrder } from '~/graphql/generated/graphql';
import { FAQ_SECTION_MAX_QUESTIONS } from '~/utils/faq';

export const useSectionFaqList = async (section: FaqSection): Promise<UseSectionFaqList> => {
  const { $urqlClient } = useNuxtApp();

  const { data, pending: fetching, error } = await useAsyncData(
    `faq-list-section-${section}`,
    () => $urqlClient.query(fetchLearningFaqsBySection, {
      first: FAQ_SECTION_MAX_QUESTIONS,
      sections: [section],
      orderBy: [{ column: LearningFaqOrderByColumn.Order, order: SortOrder.Asc }],
    }, { requestPolicy: 'cache-and-network' }).toPromise(),
    {
      server: true,
      lazy: true, // non-blocking nav - SSR mode
      transform: (data) => {
        if (data.error) {
          throw data.error;
        }

        return {
          data: (data.data?.learningFaqs?.data || []).filter(item => !!item).map((fragment) => {
            const _fragment = useFragment(learningFaqFragment, fragment);
            return {
              heading: _fragment.question,
              content: _fragment.answer,
            };
          }),
          stale: data.stale,
        };
      },
    },
  );

  const faqs = computed(() => data.value?.data ?? []);

  return {
    faqs,
    isFetching: fetching,
    error,
  };
};
